class CollectionQuickFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-quick-filter-btn]'));
    this.subButtons = Array.from(this.querySelectorAll('[data-sub-filter-btn]'));
    if (!this.buttons.length) return;

    this.activeClass = 'is-active';
    // Per-value "param=value" facet pairs with zero matches in the current
    // category view, rebuilt from the rendered facet counts whenever the active
    // parent changes (see collectEmptyFilterValues). Lets a sub-filter pill
    // (e.g. Sale) auto-hide when its filter has no products in the category —
    // mirrors the homepage collection experience.
    this.emptyFilterValues = new Set();
    this.lastEmptiesParent = null;
    this.titleElement = this.querySelector('[data-quick-filter-title]');
    this.descriptionElement = this.querySelector('[data-quick-filter-description]');
    this.showcaseTarget = this.querySelector('[data-quick-filter-showcase]');
    this.handleClick = this.onButtonClick.bind(this);
    this.handleSubClick = this.onSubButtonClick.bind(this);
    this.handlePopState = this.syncActiveState.bind(this);

    this.buttons.forEach((button) => button.addEventListener('click', this.handleClick));
    this.subButtons.forEach((button) => button.addEventListener('click', this.handleSubClick));
    window.addEventListener('popstate', this.handlePopState);
    this.observeGridChanges();
    this.syncActiveState();

    // Pre-seed the initial, server-rendered category into the grid cache during
    // idle time, so the shopper's first pill click doesn't pay the whole-document
    // serialize inline (cacheCurrentGrid then no-ops on click). Falls back to a
    // short timeout where requestIdleCallback is unavailable (Safari).
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => this.cacheCurrentGrid());
  }

  disconnectedCallback() {
    if (this.buttons) {
      this.buttons.forEach((button) => button.removeEventListener('click', this.handleClick));
    }
    if (this.subButtons) {
      this.subButtons.forEach((button) => button.removeEventListener('click', this.handleSubClick));
    }
    window.removeEventListener('popstate', this.handlePopState);
    if (this.gridObserver) this.gridObserver.disconnect();
  }

  observeGridChanges() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    let timer;
    this.gridObserver = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (this.selfInitiatedSwap) {
          // A pill / sub-filter click drove this swap and already set the active
          // parent + sub from the params it built. The only post-swap work that
          // needs the freshly-rendered facet counts is refreshing which
          // sub-filters are empty — skip the full URL re-derivation (matchesButton
          // loops over every pill, active-state writes, the override-redirect check).
          this.selfInitiatedSwap = false;
          const activeButton = this.buttons.find((btn) => btn.classList.contains(this.activeClass));
          this.refreshEmpties(activeButton);
        } else {
          // External grid change (facet-drawer apply, sort, active-filter-tag
          // removal) — re-sync the pills' active state from the URL.
          this.syncActiveState();
        }
      }, 150);
    });
    this.gridObserver.observe(grid, { childList: true, subtree: true });
  }

  onButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;

    // Cache the outgoing grid (the initial server-rendered category included) before
    // switching, so returning to it later is an instant cache hit, not a fresh fetch.
    this.cacheCurrentGrid();

    const subToApply = this.getDefaultSubButton();
    const searchParams = this.buildSearchParams(button, subToApply);

    if (typeof FacetFiltersForm !== 'undefined') {
      // Mark this swap as self-initiated so the grid MutationObserver skips the
      // redundant full syncActiveState (we set the active state below ourselves).
      this.selfInitiatedSwap = true;
      FacetFiltersForm.renderPage(searchParams.toString());
      this.notifyUrlChanged();
    } else {
      const queryString = searchParams.toString();
      const target = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      window.location.assign(target);
      return;
    }

    this.setActiveButton(button);
    this.setActiveSubButton(subToApply);
    this.updateSubFilterVisibility(button);
  }

  updateSubFilterVisibility(parentButton) {
    if (!this.subButtons.length) return;

    const hideList = (parentButton.dataset.hideSubfilters || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    this.subButtons.forEach((subBtn) => {
      const label = subBtn.textContent.trim().toLowerCase();
      const shouldHide = hideList.includes(label) || this.isSubFilterEmpty(subBtn);
      subBtn.style.display = shouldHide ? 'none' : '';
    });

    // If active sub-filter is now hidden, reset to default
    const activeSubBtn = this.subButtons.find((btn) => btn.classList.contains(this.activeClass));
    if (activeSubBtn && activeSubBtn.style.display === 'none') {
      const visibleDefault = this.subButtons.find(
        (btn) => btn.style.display !== 'none' && btn.dataset.default === 'true'
      );
      const firstVisible = this.subButtons.find((btn) => btn.style.display !== 'none');
      const newActive = visibleDefault || firstVisible;
      if (newActive) {
        this.applySubFilter(newActive);
      }
    }
  }

  // Recompute which sub-filters are empty against the CURRENT (freshly-rendered)
  // facet counts and re-apply sub-filter visibility. Counts are only recomputed
  // when the category/parent actually changed: a parent switch resets to its
  // sort-only default sub, so the facet counts reflect the category alone; a
  // sub-filter click keeps the same parent, so reusing the cached counts avoids
  // judging emptiness against a narrowed sub-filtered view (which would wrongly
  // hide sibling sub-filters). This is the one piece of post-swap sync that
  // depends on the swapped-in DOM, so both syncActiveState and the self-initiated
  // observer path route through it.
  refreshEmpties(activeButton) {
    if (!activeButton) return;
    if (activeButton !== this.lastEmptiesParent) {
      this.emptyFilterValues = this.collectEmptyFilterValues();
      this.lastEmptiesParent = activeButton;
    }
    this.updateSubFilterVisibility(activeButton);
  }

  // Read per-value product counts from the live facet drawer. facets.liquid marks
  // a count-0 (and inactive) value with a `disabled` class on its .facets__label
  // and carries the raw count in `data-count` — both are checked (no regex over
  // the label text). facets.js refreshes these elements (renderFilters) before
  // swapping the grid, so by the time the grid MutationObserver fires the counts
  // read here are current. Returns a Set of "param=value" strings (e.g.
  // "filter.p.tag=sale") that have no matches in the current category view.
  collectEmptyFilterValues() {
    const empty = new Set();
    // Also record EVERY rendered facet value so a gated sub-filter (Sale) can be
    // hidden when its value is ABSENT (Shopify omits a filter value with zero
    // products, so "absent" = "nothing on sale"). Stored on this.presentFilterValues
    // for isSubFilterEmpty. On this page the server-side gate usually removes the
    // gated button already, so this is a client-side safety net.
    const present = new Set();
    document.querySelectorAll('label.facets__label').forEach((label) => {
      const input = label.querySelector('input[name^="filter."]');
      if (!input || !input.name || !input.value) return;
      present.add(`${input.name}=${input.value}`);
      let isEmpty = label.classList.contains('disabled');
      if (!isEmpty) {
        const count = parseInt(label.dataset.count, 10);
        if (!Number.isNaN(count) && count === 0) isEmpty = true;
      }
      if (isEmpty) empty.add(`${input.name}=${input.value}`);
    });
    this.presentFilterValues = present;
    return empty;
  }

  // A sub-filter is "empty" when a `filter.` pair in its query has zero matches
  // in the current category view. Sort-only sub-filters (Best Sellers, New
  // Arrivals) carry no `filter.` param, so they are never treated as empty.
  isSubFilterEmpty(subBtn) {
    // Gated sub-filter (opts in via data-filter-param/value): show only when its
    // value is present WITH products; hide on a zero count OR total absence (e.g.
    // nothing on sale). Guarded by a non-empty present-set so we never over-hide
    // when no facets rendered. On this page the server-side gate usually removes
    // the button already — this is a client-side safety net + keeps parity with
    // the homepage switcher.
    const gateParam = subBtn.dataset.filterParam;
    const gateValue = subBtn.dataset.filterValue;
    if (gateParam && gateValue) {
      // Keep a gated pill hidden until facet data proves its value has products.
      // On this page facets are in the initial DOM, so presentFilterValues is
      // already populated before the first updateSubFilterVisibility — a present
      // pill isn't hidden. Kept in parity with the homepage switcher.
      if (!this.presentFilterValues || !this.presentFilterValues.size) return true;
      const gateKey = `${gateParam}=${gateValue}`;
      if (!this.presentFilterValues.has(gateKey)) return true; // absent → no products
      if (this.emptyFilterValues && this.emptyFilterValues.has(gateKey)) return true; // present, count 0
      return false; // present with products → reveal
    }

    if (!this.emptyFilterValues || !this.emptyFilterValues.size) return false;
    const params = new URLSearchParams(subBtn.dataset.query || '');
    for (const [key, value] of params) {
      if (key.indexOf('filter.') === 0 && this.emptyFilterValues.has(`${key}=${value}`)) {
        return true;
      }
    }
    return false;
  }

  onSubButtonClick(event) {
    event.preventDefault();
    const subButton = event.currentTarget;
    if (subButton.classList.contains(this.activeClass)) return;

    this.cacheCurrentGrid();
    this.applySubFilter(subButton);
  }

  // Apply a sub-filter under the active parent: build its params and issue a
  // single grid render. Shared by the sub-filter click handler and the visibility
  // corrector — the latter previously fired a synthetic newActive.click(), which
  // re-ran cacheCurrentGrid + the whole click pipeline (and a stray DOM click
  // event) for a redundant second fetch+swap in the same interaction.
  applySubFilter(subButton) {
    if (!subButton) return;

    const activeParentButton = this.buttons.find((btn) => btn.classList.contains(this.activeClass)) || this.buttons[0];
    const searchParams = this.buildSearchParams(activeParentButton, subButton);

    if (typeof FacetFiltersForm !== 'undefined') {
      // Self-initiated swap — see onButtonClick.
      this.selfInitiatedSwap = true;
      FacetFiltersForm.renderPage(searchParams.toString());
      this.notifyUrlChanged();
    } else {
      const queryString = searchParams.toString();
      const target = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      window.location.assign(target);
      return;
    }

    this.setActiveSubButton(subButton);
  }

  notifyUrlChanged() {
    document.dispatchEvent(new CustomEvent('collection:url-changed', { detail: { url: window.location.href } }));
  }

  getDefaultSubButton() {
    if (!this.subButtons.length) return null;
    return this.subButtons.find((btn) => btn.dataset.default === 'true') || this.subButtons[0];
  }

  // Snapshot the currently displayed grid into FacetFiltersForm's cache under the
  // active parent+sub filter key, so returning to it — notably the initial,
  // server-rendered category, which is never otherwise cached — is instant. The
  // key is built via buildSearchParams (the same path a click uses), so it matches
  // the later lookup; the facets helper no-ops if that key is already cached.
  cacheCurrentGrid() {
    if (typeof FacetFiltersForm === 'undefined' || typeof FacetFiltersForm.cacheCurrentSection !== 'function') return;
    const activeParent = this.buttons.find((btn) => btn.classList.contains(this.activeClass));
    if (!activeParent) return;
    const activeSub = this.subButtons.find((btn) => btn.classList.contains(this.activeClass)) || this.getDefaultSubButton();
    FacetFiltersForm.cacheCurrentSection(this.buildSearchParams(activeParent, activeSub).toString());
  }

  buildSearchParams(button, subButton = null) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    // Clear keys owned by ANY parent pill so swapping pills swaps the category
    // (including swapping to ALL, whose own data-query is empty). Drawer-applied
    // keys not owned by any pill are preserved.
    this.buttons.forEach((btn) => {
      this.parseParams(btn.dataset.query).forEach((_, key) => params.delete(key));
    });
    this.getClearParams(button).forEach((key) => params.delete(key));
    if (subButton) {
      this.subButtons.forEach((btn) => {
        this.parseParams(btn.dataset.query).forEach((_, key) => params.delete(key));
        if (btn.dataset.queryAllOverride) {
          this.parseParams(btn.dataset.queryAllOverride).forEach((_, key) => params.delete(key));
        }
      });
      this.getClearParams(subButton).forEach((key) => params.delete(key));
    }

    // Apply parent filter params
    const buttonParams = this.parseParams(button.dataset.query);
    buttonParams.forEach((value, key) => {
      if (value === '') {
        params.delete(key);
      } else {
        params.append(key, value);
      }
    });

    // Apply sub-filter params (use ALL override when the default parent is active)
    if (subButton) {
      const useAllOverride = button.dataset.default === 'true' && subButton.dataset.queryAllOverride;
      const subQuery = useAllOverride ? subButton.dataset.queryAllOverride : subButton.dataset.query;
      const subParams = this.parseParams(subQuery);
      subParams.forEach((value, key) => {
        if (value === '') {
          params.delete(key);
        } else {
          params.append(key, value);
        }
      });
    }

    return params;
  }

  parseParams(queryString) {
    return new URLSearchParams(queryString || '');
  }

  getClearParams(button) {
    const extra = button.dataset.clear || '';
    return extra
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
  }

  getControlledKeys() {
    if (this.controlledKeys) return this.controlledKeys;
    const keys = new Set(['page']);

    this.buttons.forEach((button) => {
      const params = this.parseParams(button.dataset.query);
      params.forEach((_, key) => keys.add(key));
      this.getClearParams(button).forEach((key) => keys.add(key));
    });

    this.subButtons.forEach((button) => {
      const params = this.parseParams(button.dataset.query);
      params.forEach((_, key) => keys.add(key));
      if (button.dataset.queryAllOverride) {
        const overrideParams = this.parseParams(button.dataset.queryAllOverride);
        overrideParams.forEach((_, key) => keys.add(key));
      }
      this.getClearParams(button).forEach((key) => keys.add(key));
    });

    this.controlledKeys = Array.from(keys);
    return this.controlledKeys;
  }

  matchesButton(button, currentParams, activeParent = null) {
    const queries = [button.dataset.query];
    if (activeParent && activeParent.dataset.default === 'true' && button.dataset.queryAllOverride) {
      queries.push(button.dataset.queryAllOverride);
    }

    for (const query of queries) {
      const buttonParams = this.parseParams(query);

      if (!buttonParams.toString()) {
        if (!this.getControlledKeys().some((key) => currentParams.has(key))) return true;
        continue;
      }

      let matches = true;
      buttonParams.forEach((value, key) => {
        const values = currentParams.getAll(key);
        if (!values.includes(value)) {
          matches = false;
        }
      });
      if (matches) return true;
    }

    return false;
  }

  syncActiveState() {
    if (!this.buttons || !this.buttons.length) return;
    const currentParams = new URLSearchParams(window.location.search);

    // When the URL contains params from a sub-filter's ALL override, the
    // default/ALL parent should stay active — not the parent whose category
    // happens to partially match the override's inclusion list.
    const defaultButton = this.buttons.find((btn) => btn.dataset.default === 'true');
    const allOverrideActive = defaultButton && this.subButtons.some((btn) => {
      if (!btn.dataset.queryAllOverride) return false;
      const overrideParams = this.parseParams(btn.dataset.queryAllOverride);
      let matches = true;
      overrideParams.forEach((value, key) => {
        if (!currentParams.getAll(key).includes(value)) matches = false;
      });
      return matches;
    });

    // Sync parent filter
    const activeButton = allOverrideActive
      ? defaultButton
      : (this.buttons.find((button) => this.matchesButton(button, currentParams)) ||
         defaultButton ||
         this.buttons[0]);

    if (activeButton) {
      this.setActiveButton(activeButton);
      this.refreshEmpties(activeButton);
    }

    // Sync sub-filter
    if (this.subButtons.length) {
      const activeSubButton =
        this.subButtons.find((button) => this.matchesButton(button, currentParams, activeButton)) ||
        this.subButtons.find((button) => button.dataset.default === 'true') ||
        this.subButtons[0];

      if (activeSubButton) {
        this.setActiveSubButton(activeSubButton);

        // When the default/ALL parent is active and the sub-filter has an ALL
        // override, ensure the URL reflects the override params. This handles
        // direct navigation (e.g. ?sort_by=created-descending) where the
        // override categories aren't in the URL yet.
        if (
          activeButton &&
          activeButton.dataset.default === 'true' &&
          activeSubButton.dataset.queryAllOverride
        ) {
          const overrideParams = this.parseParams(activeSubButton.dataset.queryAllOverride);
          let needsRedirect = false;
          overrideParams.forEach((value, key) => {
            if (!currentParams.getAll(key).includes(value)) {
              needsRedirect = true;
            }
          });
          if (needsRedirect) {
            const correctedParams = this.buildSearchParams(activeButton, activeSubButton);
            if (typeof FacetFiltersForm !== 'undefined') {
              FacetFiltersForm.renderPage(correctedParams.toString());
              this.notifyUrlChanged();
            } else {
              const qs = correctedParams.toString();
              window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
            }
          }
        }
      }
    }
  }

  setActiveButton(activeButton) {
    this.buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle(this.activeClass, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.updateMeta(activeButton);
    this.updateShowcase(activeButton);
  }

  setActiveSubButton(activeSubButton) {
    if (!activeSubButton) return;
    this.subButtons.forEach((button) => {
      const isActive = button === activeSubButton;
      button.classList.toggle(this.activeClass, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updateMeta(button) {
    if (!button) return;

    if (this.titleElement) {
      this.titleElement.textContent = button.dataset.heading || this.titleElement.dataset.defaultHeading || '';
    }

    if (this.descriptionElement) {
      const description = button.dataset.description || '';
      this.descriptionElement.textContent = description;
      this.descriptionElement.toggleAttribute('hidden', description === '');
    }
  }

  updateShowcase(button) {
    if (!this.showcaseTarget) return;
    const templateId = button?.dataset.showcaseTemplate;
    if (templateId) {
      const template = document.getElementById(templateId);
      if (template) {
        this.showcaseTarget.innerHTML = template.innerHTML;
        this.showcaseTarget.removeAttribute('hidden');
        return;
      }
    }
    this.showcaseTarget.innerHTML = '';
    this.showcaseTarget.setAttribute('hidden', '');
  }
}

if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', CollectionQuickFilters);
}
